import { useEffect, useMemo, useState } from "react";
import {
  ancestorIds,
  buildFolderTree,
  folderContainsDoc,
  type FolderTreeNode,
  type ViewerDoc,
  type ViewerFolder,
} from "../lib/viewerFolderTree";
import { FileTypeIcon, IconChevronRight, IconFolder, IconSpace } from "./Icons";

function docLabel(d: ViewerDoc) {
  return d.display_name?.trim() || d.filename;
}

type FolderTreeBranchProps = {
  node: FolderTreeNode;
  depth: number;
  expandedIds: Set<string>;
  activeDocId: string | null;
  activeFolderId: string | null;
  folderAncestors: Set<string>;
  screen: "home" | "document";
  onToggle: (id: string) => void;
  onSelectFolder: (id: string) => void;
  onSelectDoc: (id: string) => void;
};

function FolderTreeBranch({
  node,
  depth,
  expandedIds,
  activeDocId,
  activeFolderId,
  folderAncestors,
  screen,
  onToggle,
  onSelectFolder,
  onSelectDoc,
}: FolderTreeBranchProps) {
  const expanded = expandedIds.has(node.folder.id);
  const isActive = screen === "home" && activeFolderId === node.folder.id;
  const isAncestor =
    screen === "home" && Boolean(activeFolderId) && folderAncestors.has(node.folder.id) && !isActive;
  const isDocAncestor = folderContainsDoc(node, activeDocId);

  return (
    <div role="group">
      <div
        className={[
          "viewer-folder-tree__row",
          "viewer-folder-tree__row--folder",
          isActive ? "is-active" : "",
          isAncestor || isDocAncestor ? "is-ancestor" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        style={{ paddingLeft: depth * 12 + 6 }}
      >
        <button
          type="button"
          className={`viewer-folder-tree__chevron${expanded ? " is-expanded" : ""}`}
          aria-expanded={expanded}
          aria-label={expanded ? `Collapse ${node.folder.name}` : `Expand ${node.folder.name}`}
          onClick={() => onToggle(node.folder.id)}
        >
          <IconChevronRight />
        </button>
        <button
          type="button"
          className="viewer-folder-tree__label-btn"
          onClick={() => onSelectFolder(node.folder.id)}
        >
          <IconFolder className="viewer-folder-tree__icon" />
          <span className="viewer-folder-tree__label">{node.folder.name}</span>
        </button>
      </div>
      {expanded ? (
        <div className="viewer-folder-tree__children">
          {node.children.map((child) => (
            <FolderTreeBranch
              key={child.folder.id}
              node={child}
              depth={depth + 1}
              expandedIds={expandedIds}
              activeDocId={activeDocId}
              activeFolderId={activeFolderId}
              folderAncestors={folderAncestors}
              screen={screen}
              onToggle={onToggle}
              onSelectFolder={onSelectFolder}
              onSelectDoc={onSelectDoc}
            />
          ))}
          {node.documents.map((d) => (
            <button
              key={d.id}
              type="button"
              className={`viewer-folder-tree__row viewer-folder-tree__row--file${activeDocId === d.id ? " is-active" : ""}`}
              style={{ paddingLeft: (depth + 1) * 12 + 6 }}
              onClick={() => onSelectDoc(d.id)}
            >
              <span className="viewer-folder-tree__chevron viewer-folder-tree__chevron--spacer" />
              <FileTypeIcon name={d.filename} className="viewer-folder-tree__file-icon" />
              <span className="viewer-folder-tree__label">{docLabel(d)}</span>
              {d.isNew ? <span className="badge">New</span> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

type ViewerFolderTreeProps = {
  folders: ViewerFolder[];
  documents: ViewerDoc[];
  activeDocId: string | null;
  activeFolderId: string | null;
  screen: "home" | "document";
  query: string;
  searchHits: ViewerDoc[] | null;
  searching: boolean;
  onSelectDoc: (docId: string) => void;
  onSelectFolder: (folderId: string) => void;
  onGoHome: () => void;
  onCloseNav?: () => void;
};

export function ViewerFolderTree({
  folders,
  documents,
  activeDocId,
  activeFolderId,
  screen,
  query,
  searchHits,
  searching,
  onSelectDoc,
  onSelectFolder,
  onGoHome,
  onCloseNav,
}: ViewerFolderTreeProps) {
  const { roots, rootDocs } = useMemo(() => buildFolderTree(folders, documents), [folders, documents]);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());
  const folderAncestors = useMemo(
    () => new Set(activeFolderId ? ancestorIds(activeFolderId, folders) : []),
    [activeFolderId, folders],
  );

  useEffect(() => {
    if (!activeDocId) return;
    const doc = documents.find((d) => d.id === activeDocId);
    const chain = ancestorIds(doc?.folder_id ?? null, folders);
    if (!chain.length) return;
    setExpandedIds((prev) => new Set([...prev, ...chain]));
  }, [activeDocId, documents, folders]);

  useEffect(() => {
    if (!activeFolderId) return;
    const chain = ancestorIds(activeFolderId, folders);
    if (!chain.length) return;
    setExpandedIds((prev) => new Set([...prev, ...chain]));
  }, [activeFolderId, folders]);

  const navSearching = Boolean(query.trim());
  const globalSearchDocs = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    if (searchHits) return searchHits;
    return documents.filter((d) =>
      `${d.filename} ${d.display_name ?? ""}`.toLowerCase().includes(q),
    );
  }, [documents, query, searchHits]);

  const toggle = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <nav className="viewer-folder-tree" aria-label="Documents">
      <button
        type="button"
        className={`viewer-folder-tree__row viewer-folder-tree__home${screen === "home" && !activeFolderId ? " is-active" : ""}`}
        onClick={() => {
          onGoHome();
          onCloseNav?.();
        }}
      >
        <span className="viewer-folder-tree__chevron viewer-folder-tree__chevron--spacer" />
        <IconSpace className="viewer-folder-tree__icon" />
        <span className="viewer-folder-tree__label">Space overview</span>
      </button>

      <div className="viewer-folder-tree__divider" />

      {navSearching ? (
        <>
          <p className="viewer-folder-tree__search-meta">
            {searching
              ? "Searching…"
              : `${globalSearchDocs.length} match${globalSearchDocs.length === 1 ? "" : "es"} across all folders`}
          </p>
          {globalSearchDocs.map((d) => (
            <button
              key={d.id}
              type="button"
              className={`viewer-folder-tree__row viewer-folder-tree__row--file${activeDocId === d.id ? " is-active" : ""}`}
              onClick={() => onSelectDoc(d.id)}
            >
              <span className="viewer-folder-tree__chevron viewer-folder-tree__chevron--spacer" />
              <FileTypeIcon name={d.filename} className="viewer-folder-tree__file-icon" />
              <span className="viewer-folder-tree__label-col">
                <span className="viewer-folder-tree__label">{docLabel(d)}</span>
                {d.folder_name ? <span className="viewer-folder-tree__meta">{d.folder_name}</span> : null}
                {d.snippet ? <span className="search-snippet">{d.snippet}</span> : null}
              </span>
            </button>
          ))}
        </>
      ) : (
        <div role="tree">
          {roots.map((node) => (
            <FolderTreeBranch
              key={node.folder.id}
              node={node}
              depth={0}
              expandedIds={expandedIds}
              activeDocId={activeDocId}
              activeFolderId={activeFolderId}
              folderAncestors={folderAncestors}
              screen={screen}
              onToggle={toggle}
              onSelectFolder={onSelectFolder}
              onSelectDoc={onSelectDoc}
            />
          ))}
          {rootDocs.map((d) => (
            <button
              key={d.id}
              type="button"
              className={`viewer-folder-tree__row viewer-folder-tree__row--file${activeDocId === d.id ? " is-active" : ""}`}
              onClick={() => onSelectDoc(d.id)}
            >
              <span className="viewer-folder-tree__chevron viewer-folder-tree__chevron--spacer" />
              <FileTypeIcon name={d.filename} className="viewer-folder-tree__file-icon" />
              <span className="viewer-folder-tree__label">{docLabel(d)}</span>
              {d.isNew ? <span className="badge">New</span> : null}
            </button>
          ))}
        </div>
      )}
    </nav>
  );
}
